import React, { useCallback, useState, useMemo } from 'react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { ClassNameMap } from '@mui/styles';
import { useIntl, FormattedMessage } from 'react-intl';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { TimeSignalClassification } from 'graphql/types';
import { useMeContext } from 'modules/me';
import { useFeatureFlag } from 'modules/common/hooks';
import { SimpleDialog } from 'modules/common/components';
import { useMessages } from 'modules/intl';
import { Delay } from 'modules/common/components/Render';
import NotesTimelineDaySkeleton from 'modules/loading/NotesTimelineDaySkeleton';
import NoteSortOrderSelector from './NoteSortOrderSelector';
import {
  useNotesFilterContext,
  NOTES_PAGE_TYPE,
  NotesClassification
} from './NotesFilterContextProvider';
import NotesSearchBox from './NotesSearchBox';
import { NotesDateRangeSelector } from './NotesDateRangeSelector';
import NotesListPageContent from './list/NotesListPageContent';
const loadTimeline = () => import('./NotesTimelinePageContent');
const NotesTimelinePageContent = React.lazy(loadTimeline);
import NotesPageTabs from './NotesPageTabs';
import MyWorkTabs from './MyWorkTabs';
import { NotesGroupBySelector } from './NotesGroupBySelector';
import { useDeleteAllTimeSignals } from './hooks/useDeleteAllTimeSignals';
import { useTimeSignalsCount } from './hooks/useTimeSignalsCount';

const useStyles = makeStyles((theme) => ({
  searchContainer: {
    margin: theme.spacing(0),
    padding: theme.spacing(0, 4, 0, 0),
    backgroundColor: theme.palette.background.default,
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column'
  },
  banner: {
    marginTop: theme.spacing(2),
    paddingLeft: theme.spacing(2.25),
    paddingRight: theme.spacing(3.75),
    display: 'flex',
    width: '100%',
    alignItems: 'center'
  },
  bannerText: {
    color: theme.palette.text.primary
  },
  bannerButton: {
    textTransform: 'none',
    ...theme.typography.caption,
    margin: theme.spacing(-4, 0, -4, 0)
  },
  searchBox: {
    marginRight: theme.spacing(2)
  },
  div: {
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    marginLeft: theme.spacing(0),
    marginRight: theme.spacing(0)
  },
  divSearch: {
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    marginTop: theme.spacing(2),
    marginLeft: theme.spacing(5)
  },
  divider: {
    width: '100%'
  },
  alert: {
    height: theme.spacing(6.5)
  }
}));

const useDialogStyles = makeStyles((theme) => ({
  title: {
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: theme.spacing(2.8),
    lineHeight: 1.6,
    fontFamily: theme.typography.h4.fontFamily
  },
  primaryButton: {
    color: theme.palette.common.white,
    marginLeft: theme.spacing(2)
  },
  messages: {
    color: theme.palette.text.primary
  },
  root: {
    '& .MuiDialog-paper': {
      paddingRight: theme.spacing(2),
      paddingBottom: theme.spacing(1)
    }
  },
  secondaryButton: {
    color: theme.palette.primary.main
  }
}));
const useTabsOverrideStyles = makeStyles(() => ({
  root: {
    width: '100%'
  }
}));

type NotesListTrashBannerType = {
  classes?: ClassNameMap;
  classification: NotesClassification;
  setIsDeleteTrashClicked: React.Dispatch<React.SetStateAction<boolean>>;
  isDeleteTrashClicked: boolean;
};
const trashDialogMessages = [
  'note.empty.trash.messageTitle',
  'note.empty.trash.messageSubtitle'
];

export const NotesTrashBanner: React.FC<NotesListTrashBannerType> = ({
  classes: overrideClasses,
  classification,
  setIsDeleteTrashClicked,
  isDeleteTrashClicked
}: NotesListTrashBannerType) => {
  const classes = useStyles({
    classes: overrideClasses
  });
  const { formatMessage } = useIntl();
  const [openDialog, setOpenDialog] = useState(false);
  const { countByType } = useTimeSignalsCount();
  const { onTimeSignalsDelete } = useDeleteAllTimeSignals(
    TimeSignalClassification.Trash
  );

  const handleDialogClose = useCallback(() => {
    setOpenDialog(false);
  }, []);
  const deleteAllNotes = useCallback(async () => {
    setOpenDialog(false);
    setIsDeleteTrashClicked(true);
    await onTimeSignalsDelete();
    setIsDeleteTrashClicked(false);
  }, [onTimeSignalsDelete, setIsDeleteTrashClicked]);
  const handleDeleteNotes = useCallback(() => {
    setOpenDialog(true);
  }, []);
  const dialogStyles = useDialogStyles();
  const trashMessages = useMessages(trashDialogMessages);
  const messages = useMemo(() => {
    return [
      trashMessages['note.empty.trash.messageTitle'],
      trashMessages['note.empty.trash.messageSubtitle']
    ];
  }, [trashMessages]);
  if (classification !== 'Trash') {
    return <></>;
  }
  return (
    <div className={classes.banner}>
      <Stack sx={{ width: '100%' }}>
        <Alert
          icon={<InfoOutlinedIcon />}
          severity="info"
          className={classes.alert}
        >
          <Typography
            data-testid="notes-list-trash-banner"
            className={classes.bannerText}
            variant={'caption'}
          >
            <FormattedMessage
              id={`note.empty.${TimeSignalClassification.Trash.toLowerCase()}.banner`}
            />
          </Typography>
          {countByType(classification) && !isDeleteTrashClicked ? (
            <Button
              className={classes.bannerButton}
              onClick={handleDeleteNotes}
            >
              <FormattedMessage id={'note.empty.trash.deleteAll'} />
            </Button>
          ) : (
            <></>
          )}
        </Alert>
      </Stack>
      <SimpleDialog
        classes={dialogStyles}
        open={openDialog}
        onSecondaryAction={handleDialogClose}
        onPrimaryAction={deleteAllNotes}
        primaryActionButtonTitle={formatMessage({
          id: 'note.empty.trash.confirmationButton'
        })}
        secondaryActionButtonTitle={formatMessage({
          id: 'note.empty.trash.cancelButton'
        })}
        title={formatMessage({ id: 'note.empty.trash.deleteDialogTitle' })}
        messages={messages}
        progressButtonProps={{ loading: false, color: 'error' }}
        onClose={handleDialogClose}
      />
    </div>
  );
};

export const NotesPageContent: React.FC = () => {
  const me = useMeContext();
  const isStartEndFieldsVisible = me?.showStartEndFields;
  const [isDeleteTrashClicked, setIsDeleteTrashClicked] = useState(false);
  const isTIWebGroupViewEnabled: boolean = useFeatureFlag(
    'isTIWebGroupViewEnabled',
    false
  );
  const classes = useStyles();
  const {
    filterContext: { pageType, selectedClassification }
  } = useNotesFilterContext();
  const tabsOverrideClasses = useTabsOverrideStyles();
  const isDeleteAllTrashClicked = useMemo(() => {
    return isDeleteTrashClicked && selectedClassification === 'Trash';
  }, [isDeleteTrashClicked, selectedClassification]);
  return (
    <>
      {/* delay added to improve loading of notes, as rendering NotesListPageContent starts earlier */}

      <Delay wait={100}>
        <div className={classes.searchContainer}>
          <Box className={classes.divider}>
            <Box sx={{ borderBottom: 1, borderColor: 'grey.300' }}>
              <div
                className={classes.div}
                onMouseEnter={loadTimeline}
                onFocus={loadTimeline}
              >
                <MyWorkTabs
                  classes={
                    pageType === NOTES_PAGE_TYPE.TIMELINE
                      ? tabsOverrideClasses
                      : undefined
                  }
                  isDeleteAllTrashClicked={isDeleteTrashClicked}
                />

                {pageType === NOTES_PAGE_TYPE.TIMELINE ? (
                  <NotesDateRangeSelector />
                ) : (
                  <>
                    {isTIWebGroupViewEnabled ? <NotesGroupBySelector /> : null}
                    {isStartEndFieldsVisible ? <NoteSortOrderSelector /> : null}
                  </>
                )}
                <NotesPageTabs />
              </div>
            </Box>
          </Box>
          <div className={classes.divSearch}>
            {pageType === NOTES_PAGE_TYPE.TIMELINE ? (
              <NotesSearchBox
                className={classes.searchBox}
                hideDaterangeFacet
              />
            ) : (
              <>
                <NotesSearchBox className={classes.searchBox} />
              </>
            )}
          </div>
        </div>
      </Delay>

      <NotesTrashBanner
        classification={selectedClassification}
        setIsDeleteTrashClicked={setIsDeleteTrashClicked}
        isDeleteTrashClicked={isDeleteTrashClicked}
      />
      {pageType === NOTES_PAGE_TYPE.LIST ? (
        <NotesListPageContent
          isDeleteAllTrashClicked={isDeleteAllTrashClicked}
        />
      ) : (
        <React.Suspense fallback={<NotesTimelineDaySkeleton />}>
          <NotesTimelinePageContent />
        </React.Suspense>
      )}
    </>
  );
};
export default React.memo(NotesPageContent);
